import { useMemo, useState } from "react";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { renderSubTitle } from "../../../components/renderComponents";
import { CustomSingleSelect } from "../../../components/common/inputFields/customSingleSelect";
import type { ExtractionImage } from "../../../types/extraction";
import { EXTRACTION_IMAGE_TYPE_LABELS } from "../../../types/extraction";
import { ExtractionImageCard } from "./ExtractionImageCard";

interface ExtractionImagesGridProps {
  images: ExtractionImage[];
  onImageError?: (image: ExtractionImage) => void;
  selectionUpdating?: boolean;
  onToggleSelected?: (image: ExtractionImage) => void;
  onSetPrimary?: (image: ExtractionImage) => void;
}

const IMAGE_TYPE_OPTIONS = [
  { id: "", label: "All types" },
  ...Object.entries(EXTRACTION_IMAGE_TYPE_LABELS).map(([id, label]) => ({
    id,
    label,
  })),
];

export function ExtractionImagesGrid({
  images,
  onImageError,
  selectionUpdating,
  onToggleSelected,
  onSetPrimary,
}: ExtractionImagesGridProps) {
  const [gptPipelineOnly, setGptPipelineOnly] = useState(false);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [marketingOnly, setMarketingOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");

  const filteredImages = useMemo(() => {
    return images.filter((img) => {
      if (gptPipelineOnly && !img.included_in_gpt_pipeline) return false;
      if (selectedOnly && !img.is_selected) return false;
      if (marketingOnly && !img.likely_marketing_image) return false;
      if (typeFilter && img.image_type !== typeFilter) return false;
      return true;
    });
  }, [images, gptPipelineOnly, selectedOnly, marketingOnly, typeFilter]);

  return (
    <Box>
      {renderSubTitle(`All Images (${filteredImages.length})`)}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          mt: 1.5,
          mb: 2,
        }}
      >
        <FormControlLabel
          sx={{
            m: 0,
            alignItems: "center",
            "& .MuiFormControlLabel-label": { fontSize: 14 },
          }}
          control={
            <Switch
              size="small"
              checked={gptPipelineOnly}
              onChange={(e) => setGptPipelineOnly(e.target.checked)}
            />
          }
          label="GPT pipeline"
        />
        <FormControlLabel
          sx={{
            m: 0,
            alignItems: "center",
            "& .MuiFormControlLabel-label": { fontSize: 14 },
          }}
          control={
            <Switch
              size="small"
              checked={selectedOnly}
              onChange={(e) => setSelectedOnly(e.target.checked)}
            />
          }
          label="Selected"
        />
        <FormControlLabel
          sx={{
            m: 0,
            alignItems: "center",
            "& .MuiFormControlLabel-label": { fontSize: 14 },
          }}
          control={
            <Switch
              size="small"
              checked={marketingOnly}
              onChange={(e) => setMarketingOnly(e.target.checked)}
            />
          }
          label="Marketing only"
        />
        <Box sx={{ minWidth: 180 }}>
          <CustomSingleSelect
            value={typeFilter}
            options={IMAGE_TYPE_OPTIONS}
            placeholder="Image type"
            labelKey="label"
            onChange={(e) => setTypeFilter(String(e.target.value))}
          />
        </Box>
      </Box>

      {filteredImages.length === 0 ? (
        <Typography color="text.secondary">
          No images match the current filters.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, minmax(0, 1fr))",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {filteredImages.map((img) => (
            <ExtractionImageCard
              key={img.id}
              image={img}
              onImageError={onImageError}
              selectable
              selectionUpdating={selectionUpdating}
              onToggleSelected={onToggleSelected}
              onSetPrimary={onSetPrimary}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
